import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { SOBRE, SERVICOS, MANIFESTO, CIDADES_ATENDIDAS, CONTATO, MARCA } from "@/content/site";

const Sobre = () => (
  <main className="pt-28 lg:pt-36">
    <section data-testid="about-hero" className="mx-auto grid max-w-7xl gap-14 px-6 pb-24 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-7">
        <SectionHeading overline={SOBRE.overline} lines={SOBRE.titulo.map((l) => <>{l}</>)} large inView={false} />
        <div className="mt-10 max-w-2xl space-y-5">
          {SOBRE.paragrafos.map((p, i) => (
            <Reveal key={i} delay={0.15 + i * 0.08}>
              <p className="text-base font-light leading-relaxed text-stone-300" data-testid={`about-paragraph-${i}`}>{p}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.4}>
          <p className="mt-10 inline-flex items-center gap-3 border border-white/10 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400">
            <span className="h-2 w-2 bg-sun" /> {MARCA.creci}
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.2} className="relative">
          <div className="overflow-hidden border border-white/10">
            <img src={SOBRE.imagem} alt={SOBRE.imagemAlt} loading="lazy" className="aspect-[4/5] w-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-4 border border-brand/40 bg-obsidian px-6 py-5 sm:-left-6">
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand">
              <MapPin size={12} /> Sede única
            </p>
            <p className="mt-2 text-sm text-stone-200">{CONTATO.cidade}/{CONTATO.estado}</p>
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="about-services" className="border-y border-white/10 bg-coal">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <SectionHeading overline="O que fazemos" lines={[<>Serviços completos</>, <>para o seu imóvel.</>]} />
        <div className="mt-14 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICOS.map((s, i) => (
            <Reveal key={s.titulo} delay={i * 0.07} className="bg-coal">
              <div className="h-full p-8 transition-colors duration-500 hover:bg-obsidian lg:p-10" data-testid={`about-service-${i}`}>
                <span className="font-mono text-sm tracking-[0.3em] text-sun">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-5 font-serif text-xl text-[#F4F4F6]">{s.titulo}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-stone-400">{s.texto}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="about-region" className="mx-auto grid max-w-7xl gap-14 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32">
      <div className="lg:col-span-5">
        <SectionHeading overline={SOBRE.regiaoOverline} lines={SOBRE.regiaoTitulo.map((l) => <>{l}</>)} texto={SOBRE.regiaoTexto} />
      </div>
      <div className="lg:col-span-7">
        <div className="grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-3">
          {CIDADES_ATENDIDAS.map((c, i) => (
            <Reveal key={c} delay={i * 0.05} className="bg-obsidian">
              <Link
                to={`/imoveis?cidade=${encodeURIComponent(c)}`}
                data-testid={`about-city-${i}`}
                className="group flex h-full items-center justify-between p-6 transition-colors duration-300 hover:bg-coal"
              >
                <span className="font-serif text-lg text-stone-200 transition-colors duration-300 group-hover:text-brand">{c}</span>
                <ArrowRight size={14} className="text-stone-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="about-manifesto" className="border-t border-white/10 bg-coal">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <SectionHeading overline={MANIFESTO.overline} lines={MANIFESTO.titulo.map((l) => <>{l}</>)} className="max-w-3xl" />
        <div className="mt-16 grid gap-px bg-white/10 md:grid-cols-2">
          {MANIFESTO.itens.map((cap, i) => (
            <Reveal key={cap.num} delay={i * 0.08} className="bg-coal">
              <div className="group h-full p-10 transition-colors duration-500 hover:bg-obsidian lg:p-14">
                <span className="font-mono text-sm tracking-[0.3em] text-sun">{cap.num}</span>
                <h3 className="mt-6 font-serif text-xl text-[#F4F4F6] sm:text-2xl">{cap.titulo}</h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-stone-400 sm:text-base">{cap.texto}</p>
                <div className="mt-8 h-px w-12 bg-brand/50 transition-[width] duration-700 group-hover:w-24" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/contato"
            data-testid="about-cta-contato"
            className="group mt-14 inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-light"
          >
            Fale com a gente
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Sobre;
